// @ts-ignore web-vitals 没有对应的 types 类型声明，所以这里需要忽略
// import { onLCP, onCLS, onFID, onFCP, onTTFB } from 'web-vitals';
import type { Metric, Sentry } from '../utils/types';

type Report = (metric: Metric) => void;

/**
 * @description 计算首屏加载时间
 * @param {Report} report 回调函数
 */
function onFSP(report: Report) {
    let observer: MutationObserver;
    let entries: Record<string, any>[] | null = [];
    // firstScreenPaint 为首屏加载时间
    let firstScreenPaint = 0;
    // 页面是否渲染完成
    let isOnLoaded = false;
    let timer: number;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    /**
     * @description 判断dom对象是否在屏幕内
     * @param {HTMLElement} dom dom对象
     * @return {boolean} 是否在屏幕内
     */
    function isInScreen(dom: HTMLElement) {
        const rectInfo = dom.getBoundingClientRect();
        if (rectInfo.left < viewportWidth && rectInfo.top < viewportHeight) {
            return true;
        }
        return false;
    }

    /**
     * @description 获取首屏渲染时间
     * @return {number} 首屏渲染时间
     */
    function getRenderTime() {
        let startTime = 0;
        entries!.forEach((entry) => {
            if (entry.startTime > startTime) {
                startTime = entry.startTime;
            }
        });
        // performance.timing.navigationStart 页面的起始时间
        return startTime - performance.timing.navigationStart;
    }

    /**
     * @description 定时器循环监听 dom 的变化，当 document.readyState === 'complete' 时，停止监听
     */
    function checkDOMChange() {
        cancelAnimationFrame(timer);
        timer = requestAnimationFrame(() => {
            if (document.readyState === 'complete') {
                isOnLoaded = true;
            }
            if (isOnLoaded) {
                // 取消监听
                observer && observer.disconnect();
                // document.readyState === 'complete' 时，计算首屏渲染时间
                firstScreenPaint = getRenderTime();
                entries = null;
                report &&
                    report({
                        name: 'FSP',
                        value: firstScreenPaint,
                        rating: firstScreenPaint > 2500 ? 'poor' : 'good',
                    });
            } else {
                checkDOMChange();
            }
        });
    }

    interface Entry {
        startTime?: number;
        children: Node[];
    }

    (function observeFirstScreenPaint() {
        const ignoreDOMList = ['STYLE', 'SCRIPT', 'LINK'];
        observer = new MutationObserver((mutationList) => {
            checkDOMChange();
            const entry: Entry = { children: [] };
            for (const mutation of mutationList) {
                if (mutation.addedNodes.length && isInScreen(mutation.target as HTMLElement)) {
                    // @ts-ignore
                    for (const node of mutation.addedNodes) {
                        // 忽略掉以上标签的变化
                        if (node.nodeType === 1 && !ignoreDOMList.includes(node.tagName) && isInScreen(node)) {
                            entry.children.push(node);
                        }
                    }
                }
            }

            if (entry.children.length) {
                entries!.push(entry);
                entry.startTime = new Date().getTime();
            }
        });
        observer.observe(document, {
            childList: true, // 监听添加或删除子节点
            subtree: true, // 监听整个子树
            characterData: true, // 监听元素的文本是否变化
            attributes: true, // 监听元素的属性是否变化
        });
    })();
}

/**
 * @description 监控页面性能
 * @param {Function} callback 回调函数，进行数据上报
 */

export function initWebVitals(sentry: Sentry) {
    function report(metric: Metric) {
        const { navigationType, value, name, rating } = metric;
        if (rating !== 'poor') return;
        sentry.captureException(new Error(`性能异常：${window.location.href} ${name} is poor`), {
            extra: {
                navigationType: navigationType ?? '',
                value,
                rating,
                name,
                pageUrl: window.location.href,
            },
        });
    }

    // LCP 最大内容绘制
    // onLCP(report, { reportAllChanges: true });

    // FCP 首次内容绘制
    // onFCP(report, { reportAllChanges: true });

    // CLS 累计布局偏移
    // onCLS(report, { reportAllChanges: true });

    // TTFB 首字节时间
    // onTTFB(report, { reportAllChanges: true });

    // 首次输入延迟
    // onFID(report, { reportAllChanges: true });

    // 首屏渲染时间
    onFSP(report);
}
